import { render } from 'preact';
import type { ComponentChild } from 'preact';
import { getConfig, resolveTheme, type PrivateContactConfig } from './config';
import { refreshAuthAndCart, getSettings, recoverCartFromUrl } from './api';
import { mountRetentionOfferIfNeeded } from './retentionOffer';
import { store } from './store';
import { AuthRoot, AuthSlotRoot, AuthChromeRoot, CartRoot, CartPageRoot, CurrencyRoot, LocaleRoot, InlineViewRoot, CheckoutInlineRoot } from './App';
import { AccountOverlay } from './components/AccountOverlay';
import { CartDrawerRoot } from './components/CartDrawer';
import { SubscribeModalRoot } from './components/SubscribeModalRoot';
import { SubscribeInlineRoot } from './components/SubscribeInlineRoot';
import { ToastContainer } from './components/ToastContainer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ContactPage, openChat } from './components/ContactPage';
import { CampaignUnsubscribePage } from './components/CampaignUnsubscribePage';
import { CampaignSubscribeConfirmPage } from './components/CampaignSubscribeConfirmPage';
import { TestimonialsPageWidget, TestimonialsShortWidget } from './components/TestimonialsWidget';
import type { AccountView } from './store';
import { applyCurrencyFromUrl, applyCurrencyToDocument, getCurrentCurrency, setCurrencySettings } from './utils/currency';
import { emit } from './analytics';
import { navigateToView as navigateShoplightView, openCartView, resolveAuthViewFromUrl } from './navigation';
import { openContextualLogin, openContextualSignup } from './returnTo';
import { t, setLocale, registerLocale, getLocale } from './i18n';
import { applyAntiBotSettings } from './antiBot';
import { handleReferralCouponEntry } from './referralCouponEntry';
import { initReferralShareBanner } from './referralShareBanner';
import { initTrackingPipeline } from './trackingPipeline';
import { initPartnerClickTracking, touchPartnerClick } from './partnerTracking';
import { captureAuthReturnFromUrl } from './returnTo';
import { authDisplaySettingsFromElement } from './authDisplay';
import {
  registerConfiguredInlinePlaceholders,
  isInlinePlaceholderConfigured,
  isInlineViewOnCurrentPage,
} from './inlinePages';
import { de } from './locales/de';
import { fr } from './locales/fr';
import { pt } from './locales/pt';
import { es } from './locales/es';
import { it } from './locales/it';
import './styles/widget.scss';

[de, fr, pt, es, it].forEach((tr, i) => registerLocale(['de', 'fr', 'pt', 'es', 'it'][i], tr));

const AUTH_PLACEHOLDER_ID = 'shop-auth-widget';
const AUTH_DESKTOP_PLACEHOLDER_ID = 'shop-auth-widget-desktop';
const CART_PLACEHOLDER_ID = 'shop-cart-widget';
const CART_DESKTOP_PLACEHOLDER_ID = 'shop-cart-widget-desktop';
const CART_PAGE_PLACEHOLDER_ID = 'shop-cart-page';
const CURRENCY_PLACEHOLDER_ID = 'shop-currency-widget';
const LOCALE_PLACEHOLDER_ID = 'shop-locale-widget';
const SUPPORT_EMAIL_PLACEHOLDER_ID = 'shop-support-email';
const SUPPORT_PHONE_PLACEHOLDER_ID = 'shop-support-phone';
const CONTACT_PAGE_PLACEHOLDER_ID = 'shop-contact-page';
const CAMPAIGN_UNSUBSCRIBE_PLACEHOLDER_ID = 'shop-campaign-unsubscribe-page';
const CAMPAIGN_SUBSCRIBE_CONFIRM_PLACEHOLDER_ID = 'shop-campaign-subscribe-confirm-page';
const SHOP_SUBSCRIBE_INLINE_PLACEHOLDER_ID = 'shop-subscribe-inline';
const TESTIMONIALS_SHORT_PLACEHOLDER_ID = 'shop-testimonials-short';
const TESTIMONIALS_PAGE_PLACEHOLDER_ID = 'shop-testimonials-page';
const CHAT_BUBBLE_PLACEHOLDER_ID = 'shop-chat-bubble';

function appendPrivateContactLink(target: HTMLElement, contact?: PrivateContactConfig, height = 16): boolean {
  if (!contact?.image_url || !contact.action_url) return false;
  const link = document.createElement('a');
  link.href = contact.action_url;
  link.style.color = 'inherit';
  link.style.display = 'inline-flex';
  link.style.alignItems = 'center';
  const img = document.createElement('img');
  img.src = contact.image_url;
  img.alt = '';
  img.setAttribute('aria-hidden', 'true');
  img.style.display = 'inline-block';
  img.style.width = 'auto';
  img.style.height = `${height}px`;
  img.style.verticalAlign = 'middle';
  link.appendChild(img);
  target.appendChild(link);
  return true;
}

const INLINE_VIEW_PLACEHOLDERS: Record<string, NonNullable<AccountView>> = {
  'shop-profile-page': 'account',
  'shop-orders-page': 'orders',
  'shop-bonuses-page': 'bonus-account',
  'shop-tickets-page': 'tickets',
  'shop-subscriptions-page': 'subscriptions',
  'shop-referral-page': 'referral-tree',
  'shop-bonus-transactions-page': 'bonus-transactions',
  'shop-discounts-page': 'discounts',
  'shop-delete-account-page': 'account-delete',
  'shop-login-page': 'checkout-login',
  'shop-signup-page': 'signup',
  'shop-reset-password-page': 'reset-password',
};
const INLINE_VIEW_PLACEHOLDER_IDS = Object.keys(INLINE_VIEW_PLACEHOLDERS);
const CHECKOUT_PLACEHOLDER_ID = 'shop-checkout-page';
const CHECKOUT_INLINE_VIEWS: NonNullable<AccountView>[] = [
  'checkout', 'checkout-qnr', 'checkout-confirm', 'order-success', 'order-return',
  'checkout-auth', 'checkout-login', 'signup',
  'account',
];

function normalizePreviewPathValue(path: string | null | undefined): string {
  const raw = path || '/';
  try {
    return (new URL(raw, window.location.origin).pathname || '/').replace(/\/+$/, '') || '/';
  } catch {
    return raw.split('?')[0].replace(/\/+$/, '') || '/';
  }
}

const TOKEN_MAP: Record<string, string> = {
  primary: '--sw-primary', secondary: '--sw-secondary',
  header_links: '--sw-header-links',
  header_links_hover: '--sw-header-links-hover', headerLinksHover: '--sw-header-links-hover',
  focus_visible_color: '--sw-focus-visible-color',
  focus_visible_width: '--sw-focus-visible-width',
  cart_badge_bg: '--sw-cart-badge-bg',
  cart_badge_text: '--sw-cart-badge-text',
  surface_bg: '--sw-surface-bg', surfaceBg: '--sw-surface-bg',
  surface_border: '--sw-surface-border', surfaceBorder: '--sw-surface-border',
  surface_shadow: '--sw-surface-shadow', surfaceShadow: '--sw-surface-shadow',
  card_text: '--sw-card-text',
  overlay_backdrop_color: '--sw-overlay-backdrop-color',
  overlay_backdrop_blur: '--sw-overlay-backdrop-blur',
  heading_font: '--sw-heading-font',
  heading_weight: '--sw-heading-weight',
  button_text_transform: '--sw-button-text-transform',
  button_bg: '--sw-button-bg',
  button_background_color: '--sw-button-bg',
  button_text: '--sw-button-text',
  button_text_color: '--sw-button-text',
  button_border: '--sw-button-border',
  button_border_color: '--sw-button-border',
  button_hover_bg: '--sw-button-hover-bg',
  button_hover_text: '--sw-button-hover-text',
  button_hover_border: '--sw-button-hover-border',
  button_focus_bg: '--sw-button-focus-bg',
  button_focus_text: '--sw-button-focus-text',
  button_active_bg: '--sw-button-active-bg',
  button_active_text: '--sw-button-active-text',
  control_height: '--sw-control-height',
  spacing_scale: '--sw-spacing-scale',
  motion_enabled: '--sw-motion-enabled',
  motion_scale: '--sw-motion-scale',
  subscribe_modal_width: '--sw-subscribe-modal-width',
  z_index_base: '--sw-z-index-base',
  success: '--sw-success', danger: '--sw-danger',
  warning: '--sw-warning', bg: '--sw-bg', background: '--sw-bg',
  text: '--sw-text', content_text: '--sw-text', column2_text: '--sw-text',
  muted: '--sw-muted',
  border: '--sw-border', radius: '--sw-radius', radiusModal: '--sw-radius-modal',
  font: '--sw-font', fontSize: '--sw-font-size', lineHeight: '--sw-line-height',
  fontWeight: '--sw-font-weight', letterSpacing: '--sw-letter-spacing', fontSizeSm: '--sw-font-size-sm', font_size_sm: '--sw-font-size-sm',
  shadow: '--sw-shadow', shadowSm: '--sw-shadow-sm', shadowModal: '--sw-shadow-modal',
  transition: '--sw-transition', focusRing: '--sw-focus-ring', linkDecoration: '--sw-link-decoration',
  spacing: '--sw-spacing', animationDuration: '--sw-animation-duration',
  card_border: '--sw-card-border',
};

/** Маппинг --sw-* → --bs-* чтобы Bootstrap-классы (btn-primary, text-success и т.д.) использовали тему */
const BS_ALIAS: Record<string, string> = {
  primary: '--bs-primary', secondary: '--bs-secondary',
  success: '--bs-success', danger: '--bs-danger', warning: '--bs-warning',
  bg: '--bs-body-bg', text: '--bs-body-color', muted: '--bs-secondary-color',
  border: '--bs-border-color',
};

function hexToRgb(hex: string): string | null {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  return m ? `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}` : null;
}

function isThemeModeDisabled(value: unknown): boolean {
  const mode = String(value ?? '').trim().toLowerCase();
  return mode === 'none' || mode === 'hide' || mode === 'hidden' || mode === 'off' || mode === 'false' || mode === '0';
}

function applyTheme(el: HTMLElement, theme?: import('./config').WidgetTheme) {
  if (!theme) return;
  for (const [key, cssVar] of Object.entries(TOKEN_MAP)) {
    if (key === 'card_border') continue; // handled below
    const val = (theme as any)[key];
    if (val != null && val !== '') {
      el.style.setProperty(cssVar, val);
      const bsVar = BS_ALIAS[key];
      if (bsVar) el.style.setProperty(bsVar, val);
    }
  }
  if (isThemeModeDisabled((theme as any).card_border)) {
    el.style.setProperty('--sw-card-border', 'none');
  }
  if (isThemeModeDisabled((theme as any).card_radius)) {
    el.style.setProperty('--sw-card-radius', '0');
  }
  if (isThemeModeDisabled((theme as any).card_shadow)) {
    el.style.setProperty('--sw-card-shadow', 'none');
  }
  if (theme.primary) {
    const rgb = hexToRgb(theme.primary);
    if (rgb) el.style.setProperty('--sw-primary-rgb', rgb);
  }
  if (theme.text) {
    const rgb = hexToRgb(theme.text);
    if (rgb) el.style.setProperty('--sw-text-rgb', rgb);
  }
  // Derived motion behavior for old and new tokens.
  const motionEnabled = String((theme as any).motion_enabled ?? '1').trim();
  const motionScaleRaw = String((theme as any).motion_scale ?? '1').trim();
  const motionScaleNum = Number(motionScaleRaw);
  const motionScale = Number.isFinite(motionScaleNum) ? Math.max(0, Math.min(3, motionScaleNum)) : 1;
  el.style.setProperty('--sw-motion-enabled', motionEnabled === '0' || motionEnabled.toLowerCase() === 'false' ? '0' : '1');
  el.style.setProperty('--sw-motion-scale', String(motionScale));
  applySlButtonBridge(el);
}

/** Copy ShopLight :root button tokens into widget --sw-* vars (modals/portals live outside .sl-main-content). */
function applySlButtonBridge(el: HTMLElement) {
  const rootStyle = getComputedStyle(document.documentElement);
  const pairs: Array<[string, string]> = [
    ['--sl-button-bg', '--sw-button-bg'],
    ['--sl-button-text', '--sw-button-text'],
    ['--sl-button-border', '--sw-button-border'],
    ['--sl-button-hover-bg', '--sw-button-hover-bg'],
    ['--sl-button-hover-text', '--sw-button-hover-text'],
    ['--sl-button-hover-border', '--sw-button-hover-border'],
    ['--sl-button-focus-bg', '--sw-button-focus-bg'],
    ['--sl-button-focus-text', '--sw-button-focus-text'],
    ['--sl-button-active-bg', '--sw-button-active-bg'],
    ['--sl-button-active-text', '--sw-button-active-text'],
    ['--sl-success', '--sw-success'],
    ['--sl-danger', '--sw-danger'],
  ];
  for (const [slVar, swVar] of pairs) {
    const val = rootStyle.getPropertyValue(slVar).trim();
    if (val) el.style.setProperty(swVar, val);
  }
}

/** Mount inline/content placeholders immediately — do not wait for auth/cart refresh. */
function mountPagePlaceholders(
  config: ReturnType<typeof getConfig>,
  mountPoint: (placeholder: Element) => HTMLElement,
  cartPageEl: Element | null,
) {
  const previewPath = getPreviewProxyPath();
  const effectiveCartPageEl = cartPageEl ?? createPreviewCartFallbackMount();
  if (effectiveCartPageEl) render(<CartPageRoot />, mountPoint(effectiveCartPageEl));

  for (const [id, view] of Object.entries(INLINE_VIEW_PLACEHOLDERS)) {
    const el = document.getElementById(id);
    if (el && isInlinePlaceholderConfigured(id)) {
      store.registerInlinePlaceholder(view);
      render(<InlineViewRoot view={view} />, mountPoint(el));
    }
  }

  const checkoutEl = document.getElementById(CHECKOUT_PLACEHOLDER_ID);
  if (checkoutEl) {
    CHECKOUT_INLINE_VIEWS.forEach((v) => store.registerInlinePlaceholder(v));
    render(<CheckoutInlineRoot />, mountPoint(checkoutEl));
  }

  const supportEmailEl = document.getElementById(SUPPORT_EMAIL_PLACEHOLDER_ID);
  if (supportEmailEl instanceof HTMLElement && config.support?.email_contact) {
    supportEmailEl.textContent = '';
    appendPrivateContactLink(supportEmailEl, config.support.email_contact);
  }
  const supportPhoneEl = document.getElementById(SUPPORT_PHONE_PLACEHOLDER_ID);
  const supportPhones = Array.isArray(config.support?.phones) ? config.support.phones : [];
  if (supportPhoneEl instanceof HTMLElement && supportPhones.length) {
    supportPhoneEl.textContent = '';
    supportPhones.forEach((p, idx) => {
      if (idx > 0) supportPhoneEl.appendChild(document.createTextNode(' · '));
      appendPrivateContactLink(supportPhoneEl, p.contact);
    });
  }
  const contactPageEl = document.getElementById(CONTACT_PAGE_PLACEHOLDER_ID);
  if (contactPageEl) {
    render(<ContactPage />, mountPoint(contactPageEl));
  }
  const campaignUnsubscribePageEl = document.getElementById(CAMPAIGN_UNSUBSCRIBE_PLACEHOLDER_ID);
  if (campaignUnsubscribePageEl) {
    render(<CampaignUnsubscribePage />, mountPoint(campaignUnsubscribePageEl));
  }
  const campaignSubscribeConfirmPageEl = document.getElementById(CAMPAIGN_SUBSCRIBE_CONFIRM_PLACEHOLDER_ID);
  if (campaignSubscribeConfirmPageEl) {
    render(<CampaignSubscribeConfirmPage />, mountPoint(campaignSubscribeConfirmPageEl));
  }
  const shopSubscribeInlinePageEl = document.getElementById(SHOP_SUBSCRIBE_INLINE_PLACEHOLDER_ID);
  if (shopSubscribeInlinePageEl) {
    render(<SubscribeInlineRoot />, mountPoint(shopSubscribeInlinePageEl));
  }
  const testimonialsShortEl = document.getElementById(TESTIMONIALS_SHORT_PLACEHOLDER_ID);
  if (testimonialsShortEl) {
    render(<TestimonialsShortWidget />, mountPoint(testimonialsShortEl));
  }
  const testimonialsPageEl = document.getElementById(TESTIMONIALS_PAGE_PLACEHOLDER_ID);
  if (testimonialsPageEl && (previewPath === null || previewPath === '/testimonials')) {
    render(<TestimonialsPageWidget />, mountPoint(testimonialsPageEl));
  }

  const livechat = config.support?.livechat;
  if (livechat?.provider === 'native' && livechat.enabled !== false) {
    let chatBubbleEl = document.getElementById(CHAT_BUBBLE_PLACEHOLDER_ID);
    if (!chatBubbleEl) {
      chatBubbleEl = document.createElement('div');
      chatBubbleEl.id = CHAT_BUBBLE_PLACEHOLDER_ID;
      document.body.appendChild(chatBubbleEl);
    }
    import('./components/ChatBubble').then(({ ChatBubble }) => {
      render(<ChatBubble config={livechat} />, mountPoint(chatBubbleEl!));
    }).catch(() => {
      console.warn('[shop-widget] ChatBubble not available yet');
    });
  }
}

function mount() {
  captureAuthReturnFromUrl();
  registerConfiguredInlinePlaceholders();
  const config = getConfig();
  if (!config.apiUrl || !config.siteKey) {
    const params = new URLSearchParams(window.location.search);
    const fallbackSiteKey = config.siteKey || params.get('site_key') || '';
    const fallbackApiUrl = config.apiUrl || window.location.origin;
    if (fallbackSiteKey !== '' && fallbackApiUrl !== '') {
      const fb = { ...config, siteKey: fallbackSiteKey, apiUrl: fallbackApiUrl };
      initTrackingPipeline(fb);
      initPartnerClickTracking(fb);
    }
    console.warn('[shop-widget] data-api-url and data-site-key are required');
    return;
  }
  initTrackingPipeline(config);
  initPartnerClickTracking(config);
  initReferralShareBanner(config);
  installShopLightPublicApi(config);
  setupAddToCartDelegate(config);

  const authEl = document.getElementById(AUTH_PLACEHOLDER_ID);
  const authDesktopEl = document.getElementById(AUTH_DESKTOP_PLACEHOLDER_ID);
  const authDisplaySettings = authDisplaySettingsFromElement(authEl);
  const authDesktopDisplaySettings = authDisplaySettingsFromElement(authDesktopEl);
  const cartEl = document.getElementById(CART_PLACEHOLDER_ID);
  const cartDesktopEl = document.getElementById(CART_DESKTOP_PLACEHOLDER_ID);
  const cartPageEl = document.getElementById(CART_PAGE_PLACEHOLDER_ID);
  const currencyEl = document.getElementById(CURRENCY_PLACEHOLDER_ID);
  const localeEl = document.getElementById(LOCALE_PLACEHOLDER_ID);
  const testimonialsShortEl = document.getElementById(TESTIMONIALS_SHORT_PLACEHOLDER_ID);
  const testimonialsPageEl = document.getElementById(TESTIMONIALS_PAGE_PLACEHOLDER_ID);
  const campaignUnsubscribeEl = document.getElementById(CAMPAIGN_UNSUBSCRIBE_PLACEHOLDER_ID);
  const campaignSubscribeConfirmEl = document.getElementById(CAMPAIGN_SUBSCRIBE_CONFIRM_PLACEHOLDER_ID);
  const shopSubscribeInlineEl = document.getElementById(SHOP_SUBSCRIBE_INLINE_PLACEHOLDER_ID);
  const hasInlinePlaceholder = INLINE_VIEW_PLACEHOLDER_IDS.some((id) => document.getElementById(id));
  if (!authEl && !authDesktopEl && !cartEl && !cartDesktopEl && !cartPageEl && !currencyEl && !localeEl && !testimonialsShortEl && !testimonialsPageEl && !campaignUnsubscribeEl && !campaignSubscribeConfirmEl && !shopSubscribeInlineEl && !hasInlinePlaceholder) return;

  if (config.locale) setLocale(config.locale);

  setupOpenSubscribeDelegate();
  setupShoplightCartBridge(config);
  setupCurrencyNavigationPersistence();
  setupHeaderTriggerDelegates(config);
  setupLivechatStatusSync(config);
  setupPreviewProxyRouteStateReset();
  setupOverlayHostMenuCoordination();

  const resolvedTheme = resolveTheme(config.themePreset, config.theme);
  // Модалки/оверлеи рендерятся в document.body — чтобы видели --sw-radius и др., вешаем тему и на body
  applyTheme(document.body, resolvedTheme);

  const mountPoint = (placeholder: Element) => {
    let root = placeholder.querySelector(':scope > .shop-widget-mount') as HTMLElement | null;
    if (root) {
      applyTheme(root, resolvedTheme);
      return root;
    }
    while (placeholder.firstChild) {
      placeholder.removeChild(placeholder.firstChild);
    }
    root = document.createElement('div');
    root.className = 'shop-widget-mount';
    applyTheme(root, resolvedTheme);
    placeholder.appendChild(root);
    return root;
  };

  getSettings(config)
    .then((res) => {
      if (res.status === 0) {
        store.addToast(res.error || t('widget.unavailable'), 'error');
      } else if (res.status === 200 && res.result) {
        store.setSettings(res.result);
        applyAntiBotSettings(res.result);
        setCurrencySettings(res.result);
        applyCurrencyFromUrl();
        applyCurrencyToDocument();
        void touchPartnerClick(config);
      }
      return res;
    })
    .catch((err) => {
      console.warn('[shop-widget] Failed to load currency settings', err);
    });

  const slotsRestored = document.documentElement.getAttribute('data-sl-slots-restored') === '1';
  const shouldDeferMount = (el: Element | null) =>
    slotsRestored && !!el?.querySelector(':scope > .shop-widget-mount');
  const deferredHeaderMounts: Array<() => void> = [];
  const mountHeaderWidget = (el: Element | null, factory: () => ComponentChild) => {
    if (!el) return;
    const doMount = () => render(factory(), mountPoint(el));
    if (shouldDeferMount(el)) {
      deferredHeaderMounts.push(doMount);
    } else {
      doMount();
    }
  };

  const cartType = config.cartType ?? 'auto';
  const cartSettings = config.cartSettings;

  if (currencyEl && config.showCurrency !== false) {
    mountHeaderWidget(currencyEl, () => <CurrencyRoot />);
  }
  mountHeaderWidget(localeEl, () => <LocaleRoot />);
  if (authEl && authDesktopEl && authEl !== authDesktopEl) {
    mountHeaderWidget(authEl, () => <AuthSlotRoot displaySettings={authDisplaySettings} />);
    // Same auth UI in header; CSS hides .sl-auth-login-label only in the mobile topbar cluster.
    mountHeaderWidget(authDesktopEl, () => <AuthSlotRoot displaySettings={authDesktopDisplaySettings} />);
    if (!document.getElementById('shop-auth-chrome')) {
      const chromeHost = document.createElement('div');
      chromeHost.id = 'shop-auth-chrome';
      chromeHost.className = 'shop-widget-mount';
      document.body.appendChild(chromeHost);
      applyTheme(chromeHost, resolvedTheme);
      render(<AuthChromeRoot />, chromeHost);
    }
  } else {
    mountHeaderWidget(
      authEl || authDesktopEl,
      () => <AuthRoot displaySettings={authEl ? authDisplaySettings : authDesktopDisplaySettings} />,
    );
  }
  if (cartEl && cartEl !== authEl && cartEl !== authDesktopEl) {
    mountHeaderWidget(cartEl, () => <CartRoot variant="mobile" cartType={cartType} cartSettings={cartSettings} />);
  }
  if (cartDesktopEl && cartDesktopEl !== cartEl && cartDesktopEl !== authEl && cartDesktopEl !== authDesktopEl) {
    mountHeaderWidget(cartDesktopEl, () => <CartRoot variant="desktop" cartType={cartType} cartSettings={cartSettings} />);
  }

  mountPagePlaceholders(config, mountPoint, cartPageEl);

  const runInitialRecoveryAndRefresh = async () => {
    try {
      mountRetentionOfferIfNeeded(config);
      await recoverCartFromUrl(config);
    } catch (_) {
      // Ignore recovery errors and continue normal widget bootstrap.
    }
    await refreshAuthAndCart(config, store);
    await handleReferralCouponEntry(config);
  };

  runInitialRecoveryAndRefresh().catch(() => {}).finally(() => {
    store.setLoading(false);
    setupGeoRestrictions(config);
    deferredHeaderMounts.forEach((fn) => fn());
    if (!authEl && !authDesktopEl && !document.getElementById('shop-auth-chrome')) {
      const overlayRoot = document.createElement('div');
      overlayRoot.id = 'shop-widget-overlay-root';
      overlayRoot.className = 'shop-widget-mount';
      document.body.appendChild(overlayRoot);
      applyTheme(overlayRoot, resolvedTheme);

      const drawerRoot = document.createElement('div');
      drawerRoot.id = 'shop-widget-drawer-root';
      drawerRoot.className = 'shop-widget-mount';
      document.body.appendChild(drawerRoot);
      applyTheme(drawerRoot, resolvedTheme);

      render(
        <ErrorBoundary>
          <AccountOverlay />
          <SubscribeModalRoot />
          <ToastContainer />
        </ErrorBoundary>,
        overlayRoot
      );

      render(
        <ErrorBoundary>
          <CartDrawerRoot />
        </ErrorBoundary>,
        drawerRoot
      );
    }

    // Deep linking: ?sw_view=orders or ?reset_code=XXX (from email link) → open that view on load
    // Direct auth URLs must still work when their inline placeholder is disabled.
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const viewParam = params.get('sw_view') as AccountView;
      const resetCode = params.get('reset_code');
      const path = window.location.pathname;
      const VALID_VIEWS: AccountView[] = [
        'discounts', 'bonus-transactions', 'account', 'bonus-account',
        'referral-tree', 'orders', 'subscriptions', 'tickets', 'account-delete',
        'signup', 'cart', 'checkout', 'checkout-auth', 'checkout-login',
        'forgot-password', 'reset-password',
      ];
      // Preview proxy: detect current view from ?path= query param via viewUrls config
      const proxyPathParam = path.includes('/preview-proxy') ? params.get('path') : null;
      let proxyView: AccountView = null;
      if (proxyPathParam) {
        const proxyPath = normalizePreviewPathValue(proxyPathParam);
        const { viewUrls } = getConfig();
        for (const [vName, vUrl] of Object.entries(viewUrls ?? {})) {
          if (!vUrl) continue;
          try {
            const parsed = new URL(vUrl, window.location.origin);
            // Proxy mode: viewUrl is preview-proxy?path=/checkout/auth — compare path param
            const targetPath = normalizePreviewPathValue(parsed.searchParams.get('path') ?? parsed.pathname);
            if (targetPath === proxyPath && VALID_VIEWS.includes(vName as NonNullable<AccountView>)) {
              proxyView = vName as AccountView;
              break;
            }
          } catch { /* skip */ }
        }
      }
      const hasCheckoutInlinePlaceholder = !!document.getElementById(CHECKOUT_PLACEHOLDER_ID);
      const hasLoginInlinePlaceholder = isInlineViewOnCurrentPage('checkout-login');
      const hasResetInlinePlaceholder = isInlineViewOnCurrentPage('reset-password');
      const hasSignupInlinePlaceholder = isInlineViewOnCurrentPage('signup');
      const orderReturnMatch = path.match(/^\/order\/return\/([a-zA-Z0-9_-]+)\/(success|cancel|failure)\/?$/);
      if (orderReturnMatch) {
        store.setLastOrderId(orderReturnMatch[1]);
        store.setOrderReturnIntent(orderReturnMatch[2] as 'success' | 'cancel' | 'failure');
        store.setAccountView('order-return');
      } else if (resetCode && resetCode.length === 10 && /^[a-zA-Z0-9]+$/.test(resetCode)) {
        if (hasResetInlinePlaceholder) {
          store.setAccountView(null);
        } else {
          navigateShoplightView('reset-password');
        }
      } else if (viewParam && VALID_VIEWS.includes(viewParam)) {
        navigateShoplightView(viewParam);
      } else if (proxyView) {
        // Only set accountView for checkout flow views rendered via CheckoutInlineRoot.
        // Cart renders via CartPageRoot inline — no setAccountView needed.
        const CHECKOUT_PROXY_VIEWS: NonNullable<AccountView>[] = [
          'checkout', 'checkout-auth', 'checkout-login', 'checkout-qnr',
          'checkout-confirm', 'order-success', 'order-return', 'signup', 'reset-password',
        ];
        const shouldUseModalForProxyView = !(proxyView === 'checkout' && hasCheckoutInlinePlaceholder)
          && !(proxyView === 'checkout-auth' && hasCheckoutInlinePlaceholder)
          && !(proxyView === 'checkout-qnr' && hasCheckoutInlinePlaceholder)
          && !(proxyView === 'checkout-confirm' && hasCheckoutInlinePlaceholder)
          && !(proxyView === 'order-success' && hasCheckoutInlinePlaceholder)
          && !(proxyView === 'order-return' && hasCheckoutInlinePlaceholder)
          && !(proxyView === 'checkout-login' && hasLoginInlinePlaceholder)
          && !(proxyView === 'signup' && hasSignupInlinePlaceholder)
          && !(proxyView === 'reset-password' && hasResetInlinePlaceholder);
        if (CHECKOUT_PROXY_VIEWS.includes(proxyView as NonNullable<AccountView>) && shouldUseModalForProxyView) {
          store.setAccountView(proxyView);
        }
      } else if ((path === '/login' || path.endsWith('/login')) && !isInlineViewOnCurrentPage('checkout-login')) {
        store.setAccountView('checkout-login');
      } else if ((path === '/signup' || path.endsWith('/signup')) && !hasSignupInlinePlaceholder) {
        store.setAccountView('signup');
      }
    }
  });
}

function setupCurrencyNavigationPersistence() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const applyCurrencyToLink = (link: HTMLAnchorElement | null) => {
    if (!link) return;
    const raw = (link.getAttribute('href') || '').trim();
    if (!raw || raw.startsWith('#') || raw.startsWith('mailto:') || raw.startsWith('tel:') || raw.startsWith('javascript:')) return;
    const cur = getCurrentCurrency();
    if (!cur) return;
    try {
      const url = new URL(raw, window.location.href);
      if (url.origin !== window.location.origin) return;
      url.searchParams.set('cur', cur);
      link.setAttribute('href', url.toString());
    } catch {
      // ignore malformed hrefs
    }
  };

  document.addEventListener('pointerdown', (e) => {
    applyCurrencyToLink((e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null);
  }, true);
  document.addEventListener('click', (e) => {
    applyCurrencyToLink((e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null);
  }, true);
  window.addEventListener('shop-widget:currency-change', () => applyCurrencyToDocument());
}

function delegateAuthSlotClick(
  e: MouseEvent,
  config: ReturnType<typeof getConfig>,
  link: HTMLAnchorElement | null,
): boolean {
  const hrefRaw = (link?.getAttribute('href') || '').trim();
  const hrefLower = hrefRaw.toLowerCase();
  const auth = store.getAuth();
  const authDefaultView: AccountView = 'account';
  const resolvedByHref = resolveAuthViewFromUrl(hrefRaw);
  const dataView = (link?.getAttribute('data-sw-view') || '').trim() as AccountView;

  const openAuthView = (view: 'checkout-login' | 'signup') => {
    e.preventDefault();
    e.stopPropagation();
    if (view === 'signup') openContextualSignup();
    else openContextualLogin();
  };

  if (hrefLower === '#signup' || hrefLower === '#open-signup' || dataView === 'signup') {
    openAuthView('signup');
    return true;
  }

  if (
    hrefLower === '#login' ||
    hrefLower === '#open-login' ||
    hrefLower === '#open-auth' ||
    dataView === 'checkout-login'
  ) {
    openAuthView('checkout-login');
    return true;
  }

  if (hrefLower === '#account') {
    e.preventDefault();
    e.stopPropagation();
    navigateShoplightView(auth ? authDefaultView : 'checkout-login');
    return true;
  }

  if (resolvedByHref) {
    e.preventDefault();
    e.stopPropagation();
    if (resolvedByHref === 'signup') openContextualSignup();
    else if (resolvedByHref === 'checkout-login') openContextualLogin();
    else navigateShoplightView(resolvedByHref);
    return true;
  }

  if (hrefLower === '' || hrefLower === '#') {
    e.preventDefault();
    e.stopPropagation();
    navigateShoplightView(auth ? authDefaultView : 'checkout-login');
    return true;
  }

  if (!hrefRaw && !config.viewUrls?.['checkout-login'] && !config.viewUrls?.signup && !config.viewUrls?.account) {
    e.preventDefault();
    e.stopPropagation();
    navigateShoplightView(auth ? authDefaultView : 'checkout-login');
    return true;
  }

  return false;
}

function setupHeaderTriggerDelegates(config: ReturnType<typeof getConfig>) {
  document.addEventListener('click', (e: MouseEvent) => {
    let target = e.target as Element | null;
    if (!target) return;
    if ((target as Node).nodeType === 3) target = target.parentElement;
    if (!target) return;

    // Do not hijack clicks inside mounted widget UI (drawer/modal/dropdowns/forms).
    // Otherwise capture-phase delegation can swallow internal controls like drawer close buttons.
    if (target.closest('.shop-widget-mount,.shop-widget-portal,[class^="sw-"],[class*=" sw-"]')) return;

    // Header slots: SSR/cached markup may carry sw-* classes before Preact handlers attach.
    const livechatSlot = target.closest('.sl-bottom-bar-livechat,[data-sw-livechat]');
    if (livechatSlot) {
      e.preventDefault();
      e.stopPropagation();
      openChat(config.support?.livechat);
      return;
    }

    const cartSlot = target.closest('.sl-cart-slot,.sl-bottom-bar-widget--cart');
    if (cartSlot) {
      e.preventDefault();
      e.stopPropagation();
      openCartView();
      return;
    }

    const authSlot = target.closest('.sl-auth-slot');
    if (authSlot && !target.closest('.sl-auth-toggle,.sl-auth-menu,.dropdown-menu,.dropdown-item,button')) {
      const authLink = target.closest('a[href]') as HTMLAnchorElement | null;
      if (delegateAuthSlotClick(e, config, authLink)) return;
    }

    const link = target.closest('a[href]') as HTMLAnchorElement | null;
    if (!link && !authSlot) return;

    const swView = (target.closest('[data-sw-view], [data-widget-view]') as HTMLElement | null)
      ?.getAttribute('data-sw-view')
      ?? (target.closest('[data-sw-view], [data-widget-view]') as HTMLElement | null)?.getAttribute('data-widget-view')
      ?? '';
    const mappedView = swView as AccountView;
    if (mappedView) {
      e.preventDefault();
      e.stopPropagation();
      if (mappedView === 'cart') openCartView();
      else if (mappedView === 'checkout-login') openContextualLogin();
      else if (mappedView === 'signup') openContextualSignup();
      else navigateShoplightView(mappedView);
      return;
    }

    if (authSlot && delegateAuthSlotClick(e, config, link)) return;
  }, true);
}

type LivechatStatusDetail = {
  provider?: string;
  dept_id?: number;
  status?: 'online' | 'offline' | 'loading' | 'unknown' | string;
  online?: boolean;
};

function setupLivechatStatusSync(config: ReturnType<typeof getConfig>) {
  const livechat = config.support?.livechat;
  if (!livechat || livechat.enabled === false || livechat.provider === 'native') return;

  const apply = (detail?: LivechatStatusDetail) => {
    let status = detail?.status;
    if (!status && typeof detail?.online === 'boolean') status = detail.online ? 'online' : 'offline';
    const normalized = status === 'online' || status === 'offline' ? status : 'loading';
    document.querySelectorAll<HTMLElement>('[data-sw-livechat]').forEach((el) => {
      el.setAttribute('data-sw-livechat-status', normalized);
      el.classList.toggle('is-livechat-online', normalized === 'online');
      el.classList.toggle('is-livechat-offline', normalized === 'offline');
      const label = el.querySelector<HTMLElement>('.sl-bottom-bar-label,.sl-header-livechat-label')?.textContent?.trim() || 'Live Help';
      const suffix = normalized === 'online' ? 'Online' : normalized === 'offline' ? 'Offline' : 'Checking status';
      el.setAttribute('aria-label', `${label} (${suffix})`);
      el.setAttribute('title', `${label} (${suffix})`);
    });
  };

  const readGlobal = () => {
    const detail = (window as Window & { __SHOPLIGHT_LIVECHAT_STATUS__?: LivechatStatusDetail }).__SHOPLIGHT_LIVECHAT_STATUS__;
    apply(detail);
  };

  window.addEventListener('shoplight:livechat-status', (event) => {
    apply((event as CustomEvent<LivechatStatusDetail>).detail);
  });
  readGlobal();
  window.dispatchEvent(new Event('shoplight:request-livechat-status'));
  window.setTimeout(readGlobal, 1000);
  window.setTimeout(() => window.dispatchEvent(new Event('shoplight:request-livechat-status')), 2500);
}

function setupOverlayHostMenuCoordination() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const WIDGET_OVERLAY_CLASS = 'sw-widget-overlay-active';
  const HOST_MENU_OPEN_RE = /(^|\s)(menu[_-]?open|mobile[_-]?menu[_-]?open|sl-mobile-menu-open)(\s|$)/i;
  let suppressHostMenuCloseUntil = 0;
  let lastOverlayOpen = false;
  let lastView: AccountView = null;

  const closeWidgetOverlayForHostMenu = () => {
    const current = store.getAccountView();
    if (!current) return;
    if (isInlineViewOnCurrentPage(current)) return;
    store.setAccountView(null);
  };

  const syncOverlayState = () => {
    const view = store.getAccountView();
    const open = !!view && (view === 'cart'
      ? !isInlineViewOnCurrentPage('cart') && getConfig().cartOpenMode !== 'side_drawer'
      : !isInlineViewOnCurrentPage(view));
    if (open) document.body.classList.add(WIDGET_OVERLAY_CLASS);
    else document.body.classList.remove(WIDGET_OVERLAY_CLASS);

    if (open !== lastOverlayOpen || view !== lastView) {
      window.dispatchEvent(new CustomEvent('sw:overlay-state', { detail: { open, view } }));
      window.dispatchEvent(new CustomEvent(open ? 'sw:overlay-open' : 'sw:overlay-close', { detail: { view } }));
      lastOverlayOpen = open;
      lastView = view;
    }
  };

  const hostMenuLooksOpen = () => {
    const htmlClass = document.documentElement.className || '';
    const bodyClass = document.body.className || '';
    const bodyState = document.body.getAttribute('data-ui-state') || '';
    return HOST_MENU_OPEN_RE.test(`${htmlClass} ${bodyClass} ${bodyState}`);
  };

  const onHostMenuSignal = () => {
    if (Date.now() < suppressHostMenuCloseUntil) return;
    if (hostMenuLooksOpen()) closeWidgetOverlayForHostMenu();
  };

  const onOverlayOpenIntent = (evt: Event) => {
    const detail = (evt as CustomEvent<{ view?: AccountView }>).detail;
    if (detail?.view) {
      // Widget overlays can be opened from inside mobile menu; give host menu a
      // short window to close before menu-open observer is allowed to close it.
      suppressHostMenuCloseUntil = Date.now() + 450;
    }
  };

  window.addEventListener('sl:mobile-menu-open', onHostMenuSignal);
  window.addEventListener('shop:mobile-menu-open', onHostMenuSignal);
  window.addEventListener('mobile-menu:open', onHostMenuSignal);
  window.addEventListener('sw:overlay-open-intent', onOverlayOpenIntent as EventListener);

  const mo = new MutationObserver(() => {
    if (Date.now() < suppressHostMenuCloseUntil) return;
    if (hostMenuLooksOpen()) closeWidgetOverlayForHostMenu();
  });
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  mo.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-ui-state'] });

  store.subscribe(syncOverlayState);
  syncOverlayState();
}

/** Hard-hide Add to Cart when auth shipping country is outside product country_sets.
 *  Guests: soft notice only (no hide) so IP ≠ shipping destination does not block browsing. */
function setupGeoRestrictions(config: ReturnType<typeof getConfig>) {
  const shippingSet = config.shippingCountriesSet ?? (config as any).shipping_countries_set;
  if (!shippingSet || typeof shippingSet !== 'object') return;
  const customerCountry = (store.getAuth() as { s_country?: string } | null)?.s_country;
  const hasShippingCountry = typeof customerCountry === 'string' && customerCountry !== '';

  const isCountryInSets = (sets: string[], country: string): boolean => {
    for (const setName of sets) {
      const countries = shippingSet[setName];
      if (Array.isArray(countries) && countries.includes(country)) return true;
    }
    return false;
  };

  document.querySelectorAll<HTMLElement>('[data-product-id][data-country-sets]').forEach((el) => {
    try {
      const raw = el.getAttribute('data-country-sets');
      if (!raw) return;
      const sets = JSON.parse(raw) as string[];
      if (!Array.isArray(sets) || sets.length === 0) return;

      const wrapper = el.closest('.sl-product-add-to-cart') ?? el.parentElement;
      if (!wrapper) return;

      if (hasShippingCountry) {
        if (isCountryInSets(sets, customerCountry)) return;
        el.style.display = 'none';
        if (wrapper.getAttribute('data-geo-message') === 'true' && !wrapper.querySelector('.sl-geo-unavailable')) {
          const msg = document.createElement('span');
          msg.className = 'sl-geo-unavailable';
          msg.style.cssText = 'font-size:14px;color:var(--sl-muted, #6c757d)';
          msg.textContent = t('api.error.60001');
          wrapper.appendChild(msg);
        }
        return;
      }

      // Guest / no shipping address yet: keep button, optional soft notice on PDP.
      if (wrapper.getAttribute('data-geo-message') === 'true' && !wrapper.querySelector('.sl-geo-soft-notice')) {
        // PDP actions use flex layout; keep the notice on its own row.
        (wrapper as HTMLElement).style.flexWrap = 'wrap';
        const msg = document.createElement('span');
        msg.className = 'sl-geo-soft-notice';
        msg.style.cssText = 'display:block;flex:0 0 100%;min-width:0;margin-top:.75rem;font-size:13px;color:var(--sl-muted, #6c757d)';
        msg.textContent = t('geo.soft_notice');
        wrapper.appendChild(msg);
      }
    } catch (_) {}
  });
}

function setupOpenSubscribeDelegate() {
  document.addEventListener('click', (e: MouseEvent) => {
    const a = (e.target as Element)?.closest?.('a[href]');
    const h = (a?.getAttribute('href') || '').trim();
    if (!a || (h !== '#open-subscribe' && !h.endsWith('#open-subscribe'))) return;
    e.preventDefault();
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('widget:subscribe:open'));
  }, true);
}

function setupPreviewProxyRouteStateReset() {
  if (typeof window === 'undefined' || !window.location.pathname.includes('/preview-proxy')) return;

  const pathFromHref = (href: string | null): string | null => {
    if (!href) return null;
    try {
      const url = new URL(href, window.location.href);
      return normalizePreviewPathValue(url.searchParams.get('path') || url.pathname || '/');
    } catch {
      return null;
    }
  };

  const clearsAccountOverlay = (path: string) =>
    !/^\/(?:checkout|login|signup|reset-password|my-account)(?:\/|$)/i.test(path);

  document.addEventListener('click', (e: MouseEvent) => {
    const link = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!link) return;
    const href = (link.getAttribute('href') || '').trim();
    if (href === '#' && link.closest('.sw-cart-icon,.sw-cart-compact,.sw-cart-summary,#shop-cart-widget,#shop-cart-widget-desktop,.shop-widget-mount')) {
      return;
    }
    const targetPath = pathFromHref(link.getAttribute('href'));
    if (targetPath && clearsAccountOverlay(targetPath)) {
      store.setAccountView(null);
    }
  }, true);
}

function getPreviewProxyPath(): string | null {
  if (typeof window === 'undefined' || !window.location.pathname.includes('/preview-proxy')) return null;
  const raw = new URLSearchParams(window.location.search).get('path') || '/';
  return normalizePreviewPathValue(raw);
}

function createPreviewCartFallbackMount(): HTMLElement | null {
  const previewPath = getPreviewProxyPath();
  if (previewPath !== '/cart') return null;

  const hostIds = [
    CART_PAGE_PLACEHOLDER_ID,
    TESTIMONIALS_PAGE_PLACEHOLDER_ID,
    CHECKOUT_PLACEHOLDER_ID,
    ...INLINE_VIEW_PLACEHOLDER_IDS,
  ];
  const host = hostIds
    .map((id) => document.getElementById(id))
    .find((el): el is HTMLElement => !!el)
    ?? document.querySelector<HTMLElement>('.sl-main-content main,.sl-column-2 main,.sl-one-col main,.sl-column-2,.sl-main-content');
  if (!host) return null;

  const mount = document.createElement('div');
  mount.id = CART_PAGE_PLACEHOLDER_ID;
  mount.className = 'shop-widget-mount';
  host.appendChild(mount);
  return mount;
}

function setupShoplightCartBridge(_config: ReturnType<typeof getConfig>) {
  window.addEventListener('shoplight:cart-add-result', (e: Event) => {
    const cart = (e as CustomEvent<{ cart?: { products?: unknown[]; total?: unknown } }>).detail?.cart;
    if (!cart) return;
    void import('./api').then(({ applyCartResultToStore }) => {
      applyCartResultToStore(store, cart);
    });
  });
  window.addEventListener('shoplight:cart-add-error', (e: Event) => {
    const payload = (e as CustomEvent<{ payload?: Record<string, unknown> }>).detail?.payload;
    void import('./api').then(({ formatApiErrorFromPayload }) => {
      store.addToast(formatApiErrorFromPayload(payload ?? null), 'error');
    });
  });
}

function installShopLightPublicApi(config: ReturnType<typeof getConfig>) {
  const api = {
    async addToCart(productId: string, options?: { buyNow?: boolean; openCart?: boolean; sub1?: string }) {
      const { addToCart, applyCartResultToStore } = await import('./api');
      const res = await addToCart(config, String(productId));
      if (res.status === 200) {
        applyCartResultToStore(store, res.result as { products?: any[]; total?: unknown });
        emit('sw:add-to-cart', { product_id: String(productId), source: 'public-api', sub1: options?.sub1 || undefined });
        if (options?.buyNow) {
          navigateShoplightView('checkout');
        } else if (options?.openCart !== false) {
          openCartView();
        }
      }
      return res;
    },
    openCart() {
      openCartView();
    },
    openCheckout() {
      navigateShoplightView('checkout');
    },
    navigateToView(view: NonNullable<AccountView>) {
      navigateShoplightView(view);
    },
  };
  const win = window as typeof window & { ShopLight?: typeof api; ShopWidget?: typeof api; shopWidget?: typeof api };
  win.ShopLight = api;
  win.ShopWidget = api;
  win.shopWidget = api;
  window.dispatchEvent(new CustomEvent('shoplight:ready', { detail: { api } }));
}

function setupAddToCartDelegate(config: ReturnType<typeof getConfig>) {
  const win = window as unknown as { __SHOP_WIDGET_ADD_HANDLER_ACTIVE__?: boolean };
  if (win.__SHOP_WIDGET_ADD_HANDLER_ACTIVE__) return;
  win.__SHOP_WIDGET_ADD_HANDLER_ACTIVE__ = true;
  document.addEventListener('click', async (e: MouseEvent) => {
    let target = e.target as Element;
    if (target.nodeType === 3) target = target.parentElement as Element;

    const btn = target.closest?.('[data-product-id]');
    if (!btn || !(btn instanceof HTMLElement)) return;
    if (btn.hasAttribute('disabled')) return;
    const productId = btn.getAttribute('data-product-id');
    if (!productId) return;
    e.preventDefault();
    e.stopPropagation();

    const originalText = btn.innerHTML;
    const raw = config.add_to_cart_texts;
    const locale = getLocale();
    const perLocale = raw && typeof raw === 'object' && 'en' in raw && typeof (raw as Record<string, unknown>).en === 'object';
    const texts = perLocale
      ? ((raw as Record<string, { adding?: string; added?: string }>)[locale] ?? (raw as Record<string, { adding?: string; added?: string }>).en)
      : (raw as { adding?: string; added?: string } | undefined);
    const customAddedText = btn.dataset.addedText || texts?.added || '';
    const addingText = btn.dataset.addingText || texts?.adding || t('cart.adding');
    const addedText = customAddedText || t('cart.added');
    btn.setAttribute('disabled', 'true');
    btn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ${addingText}`;

    try {
      const { addToCart, applyCartResultToStore, formatOrderLimitToast } = await import('./api');
      const prevQty = store.getCartProducts().find((p) => String(p?.productid) === productId)?.amount ?? 0;
      const res = await addToCart(config, productId);
      if (res.status === 200) {
        const cartResult = res.result as {
          products?: { productid?: number; amount?: number; max_qty?: number; qty_limit_code?: number }[];
          total?: unknown;
          order_limit_notice?: string;
        };
        applyCartResultToStore(store, cartResult);
        const line = cartResult?.products?.find((p) => String(p?.productid) === productId);
        const newQty = line?.amount ?? 0;
        const replaceNotice = typeof cartResult?.order_limit_notice === 'string' ? cartResult.order_limit_notice.trim() : '';
        if (replaceNotice) {
          store.addToast(replaceNotice, 'info');
        } else if (Number(prevQty) > 0 && Number(newQty) === Number(prevQty)) {
          const limit = Number(line?.max_qty) > 0 ? Number(line?.max_qty) : Number(newQty);
          const code = Number(line?.qty_limit_code) === 60103 ? 60103 : 60102;
          store.addToast(formatOrderLimitToast(code, limit), 'error');
        } else {
          store.addToast(customAddedText || t('cart.addSuccess'), 'success');
        }
        emit('sw:add-to-cart', { product_id: productId });
        openCartView();
        if (addedText) btn.innerHTML = addedText;
        await new Promise((resolve) => setTimeout(resolve, 350));
        btn.innerHTML = originalText;
        btn.removeAttribute('disabled');
      } else {
        console.error('Failed to add to cart', res);
        store.addToast(res.error || t('cart.addFail', { error: 'Unknown error' }), 'error');
        btn.innerHTML = originalText;
        btn.removeAttribute('disabled');
      }
    } catch (err) {
      console.error('Error adding to cart', err);
      btn.innerHTML = originalText;
      btn.removeAttribute('disabled');
    }
  }, true);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mount);
} else {
  mount();
}
